import { useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import api from 'src/services/api';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';
import { objectToQueryString } from 'src/utils/uri';
import { APP_COUNTS_KEY } from 'src/constants/query';
import {
  DEPLOYMENT_RINGS_QUERY_KEY,
  DEPLOYMENT_RING_DEVICES_QUERY_KEY,
} from 'src/hooks/deploymentRing';

const DEVICE_QUERY_KEY = 'device';
const DEVICES_QUERY_KEY = 'devices';
const DEVICE_OS_VERSIONS_QUERY_KEY = 'deviceOsVersions';
const DEVICE_LOG_COLLECTOR_VERSIONS_QUERY_KEY = 'deviceLogCollectorVersions';
const DEVICE_WDAC_READINESS_COUNTS_QUERY_KEY = 'deviceWdacReadinessCounts';
const DEVICE_WDAC_READINESS_QUERY_KEY = 'deviceWdacReadiness';
const DEVICE_READINESS_SUMMARY_QUERY_KEY = 'deviceReadinessSummary';
const DEVICE_READINESS_APPS_QUERY_KEY = 'deviceReadinessApps';
const DEVICE_READINESS_UNMATCHED_QUERY_KEY = 'deviceReadinessUnmatched';
const DEVICE_WDAC_RESULTS_QUERY_KEY = 'deviceWdacResults';
const DEVICE_UNMATCHED_FILES_QUERY_KEY = 'deviceUnmatchedFiles';
const DEVICE_UNMATCHED_FILE_DETAILS_QUERY_KEY = 'deviceUnmatchedFileDetails';
const DEVICE_TABS_DATA_COUNTS_QUERY_KEY = 'deviceTabsDataCounts';

const getDeviceDetails = async ({ deviceId, customerEnvId }) =>
  api.get(`/device/${customerEnvId}/${deviceId}`);

const updateDeviceSettings = async ({
  deviceId,
  customerEnvId,
  status,
  notes,
  tagIds,
  tagNames,
  addTagIds,
  addTagNames,
  deploymentRingId,
}) =>
  api.put(`/device/${customerEnvId}/${deviceId}/settings`, {
    DeviceId: deviceId,
    Status: status,
    Notes: notes,
    TagIds: tagIds,
    TagNames: tagNames,
    AddTagIds: addTagIds,
    AddTagNames: addTagNames,
    DeploymentRingId: deploymentRingId,
  });

const getDevices = async ({ customerEnvId, page, limit, query, signal }) =>
  api.get(`/device/${customerEnvId}/all?${objectToQueryString(query)}`, {
    params: {
      Page: page,
      Limit: limit,
    },
    ...(signal ? { signal } : {}),
  });

const getExportDevices = async ({ customerEnvId, query }) =>
  api.get(`/device/${customerEnvId}/exportDevices?${objectToQueryString(query)}`);

// sampleSize 0 returns only the columns; -1 returns every device matching the query.
const getBulkUpdateTemplate = async ({ customerEnvId, query, sampleSize }) =>
  api.get(
    `/device/${customerEnvId}/bulkUpdateTemplate?${objectToQueryString({ ...query, sampleSize })}`,
  );

const getExportWdacReadinessDevices = async ({ customerEnvId, query }) =>
  api.get(`/device/${customerEnvId}/exportWdacReadinessDevices?${objectToQueryString(query)}`);

const updateDeviceStatus = async ({ deviceId, status, notes }) =>
  api.put('/device', {
    DeviceId: deviceId,
    Status: status,
    Notes: notes,
  });

const bulkUpdateDeviceStatus = async ({
  customerEnvId,
  deviceIds,
  status,
  notes,
  addTagIds,
  addTagNames,
}) =>
  api.post(`/device/${customerEnvId}/bulkUpdateStatus`, {
    deviceIds,
    status,
    notes,
    addTagIds,
    addTagNames,
  });

const getDeviceOsVersions = async ({ customerEnvId, search = '', page = 1, limit = 100 }) =>
  api.get(`/device/${customerEnvId}/osVersions`, {
    params: {
      ...(search ? { Search: search } : {}),
      Page: page,
      Limit: limit,
    },
  });

const getDeviceLogCollectorVersions = async ({
  customerEnvId,
  search = '',
  page = 1,
  limit = 100,
}) =>
  api.get(`/device/${customerEnvId}/logCollectorVersions`, {
    params: {
      ...(search ? { Search: search } : {}),
      Page: page,
      Limit: limit,
    },
  });

const getDeviceReadinessSummary = async ({ deviceId, customerEnvId }) =>
  api.get(`/device/${customerEnvId}/${deviceId}/readinessSummary`);

const getDeviceReadinessApps = async ({ deviceId, customerEnvId, page, limit }) =>
  api.get(`/device/${customerEnvId}/${deviceId}/readinessApps`, {
    params: {
      Page: page,
      Limit: limit,
    },
  });

const getDeviceReadinessUnmatched = async ({ deviceId, customerEnvId, page, limit }) =>
  api.get(`/device/${customerEnvId}/${deviceId}/readinessUnmatched`, {
    params: {
      Page: page,
      Limit: limit,
    },
  });

const getDeviceWdacResults = async ({ deviceId, customerEnvId, page, limit, query, signal }) =>
  api.get(`/device/${customerEnvId}/${deviceId}/wdacResults?${objectToQueryString(query)}`, {
    params: {
      Page: page,
      Limit: limit,
    },
    ...(signal ? { signal } : {}),
  });

const getDeviceUnmatchedFiles = async ({ deviceId, customerEnvId, page, limit, query, signal }) =>
  api.get(
    `/device/${customerEnvId}/${deviceId}/unmatchedFilesWdacResults?${objectToQueryString(query)}`,
    {
      params: {
        Page: page,
        Limit: limit,
      },
      ...(signal ? { signal } : {}),
    },
  );

const getDeviceUnmatchedFileDetails = async ({ deviceId, customerEnvId, fileId, signal }) =>
  api.get(`/device/${customerEnvId}/${deviceId}/unmatchedFilesWdacResults/${fileId}/details`, {
    ...(signal ? { signal } : {}),
  });

const getDeviceUnmatchedFileEvents = async ({
  deviceId,
  customerEnvId,
  fileId,
  page,
  limit,
  signal,
}) =>
  api.get(`/device/${customerEnvId}/${deviceId}/unmatchedFilesWdacResultItems`, {
    params: {
      Page: page,
      Limit: limit,
      fileId,
      'OrderWdacQuery.SortBy': 'event_system_time',
      'OrderWdacQuery.Order': 'DESC',
    },
    ...(signal ? { signal } : {}),
  });

const getDeviceTabsDataCounts = async ({ deviceId, customerEnvId }) =>
  api.get(`/device/${customerEnvId}/${deviceId}/deviceDetailsTabsDataCounts`);

const getWdacReadinessStatusCounts = async ({ customerEnvId, query = {} }) =>
  api.get(`/dashboard/${customerEnvId}/wdacReadinessStatusCounts?${objectToQueryString(query)}`);

const getWdacReadinessDevices = async ({ customerEnvId, page, limit, query = {}, signal }) =>
  api.get(`/device/${customerEnvId}/wdacReadiness?${objectToQueryString(query)}`, {
    params: {
      Page: page,
      Limit: limit,
    },
    ...(signal ? { signal } : {}),
  });

export const useInfiniteDevices = ({ apiQuery, limit = 50, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [DEVICES_QUERY_KEY, customerEnvId, apiQuery, limit],
    ({ pageParam = 1, signal }) =>
      getDevices({
        customerEnvId,
        page: pageParam,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.devices?.length || 0), 0);
        return loaded < (lastPage.count || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useExportDevices = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useMutation(({ query }) => getExportDevices({ customerEnvId, query }));
};

export const useGetBulkUpdateTemplate = () => {
  const queryClient = useQueryClient();
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useCallback(
    (query, sampleSize) =>
      queryClient.fetchQuery({
        queryKey: ['bulkUpdateTemplate', customerEnvId, query, sampleSize],
        queryFn: () => getBulkUpdateTemplate({ customerEnvId, query, sampleSize }),
        staleTime: 0,
      }),
    [queryClient, customerEnvId],
  );
};

export const useExportWdacReadinessDevices = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useMutation(({ query }) => getExportWdacReadinessDevices({ customerEnvId, query }));
};

export const useUpdateDeviceStatus = () => {
  const queryClient = useQueryClient();
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useMutation(
    ({ deviceId, status, notes }) => updateDeviceStatus({ deviceId, status, notes }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [DEVICES_QUERY_KEY], exact: false });
        await queryClient.invalidateQueries([APP_COUNTS_KEY, customerEnvId]);
      },
    },
  );
};

export const useValidateBulkDeviceUpdate = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useMutation(({ devices }) =>
    api.post(`/device/${customerEnvId}/bulkUpdateValidate`, { devices }),
  );
};

export const useApplyBulkDeviceUpdate = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useMutation(({ devices }) =>
    api.post(`/device/${customerEnvId}/bulkUpdateApply`, { devices }),
  );
};

// The update runs as several requests, so the lists are refreshed once after all of them.
export const useRefreshAfterBulkDeviceUpdate = () => {
  const queryClient = useQueryClient();
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [DEVICES_QUERY_KEY], exact: false });
    await queryClient.invalidateQueries({ queryKey: ['dashboardClientHealth'], exact: false });
    await queryClient.invalidateQueries({
      queryKey: ['dashboardClientHealthDevices'],
      exact: false,
    });
    await queryClient.invalidateQueries({ queryKey: ['dashboardEnforcement'], exact: false });
    await queryClient.invalidateQueries({
      queryKey: ['dashboardEnforcementDevices'],
      exact: false,
    });
    await queryClient.invalidateQueries([APP_COUNTS_KEY, customerEnvId]);
    await queryClient.invalidateQueries({ queryKey: ['deploymentRings'], exact: false });
  }, [queryClient, customerEnvId]);
};

export const useBulkUpdateDeviceStatus = () => {
  const queryClient = useQueryClient();
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useMutation(
    ({ deviceIds, status, notes, addTagIds, addTagNames }) =>
      bulkUpdateDeviceStatus({
        customerEnvId,
        deviceIds,
        status,
        notes,
        addTagIds,
        addTagNames,
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [DEVICES_QUERY_KEY], exact: false });
        await queryClient.invalidateQueries({
          queryKey: ['dashboardClientHealth'],
          exact: false,
        });
        await queryClient.invalidateQueries({
          queryKey: ['dashboardClientHealthDevices'],
          exact: false,
        });
        await queryClient.invalidateQueries({
          queryKey: ['dashboardEnforcement'],
          exact: false,
        });
        await queryClient.invalidateQueries({
          queryKey: ['dashboardEnforcementDevices'],
          exact: false,
        });
        await queryClient.invalidateQueries([APP_COUNTS_KEY, customerEnvId]);
      },
    },
  );
};

export const useDeviceOsVersions = ({ enabled = true } = {}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [DEVICE_OS_VERSIONS_QUERY_KEY, customerEnvId],
    () => getDeviceOsVersions({ customerEnvId }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useDeviceLogCollectorVersions = ({ enabled = true } = {}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [DEVICE_LOG_COLLECTOR_VERSIONS_QUERY_KEY, customerEnvId],
    () => getDeviceLogCollectorVersions({ customerEnvId }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useDeviceDetails = ({ deviceId }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [DEVICE_QUERY_KEY, deviceId],
    () => getDeviceDetails({ deviceId, customerEnvId }),
    {
      enabled: !!deviceId && !!customerEnvId,
    },
  );
};

export const useUpdateDeviceSettings = () => {
  const queryClient = useQueryClient();
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useMutation(
    ({ deviceId, status, notes, tagIds, tagNames, addTagIds, addTagNames, deploymentRingId }) =>
      updateDeviceSettings({
        deviceId,
        customerEnvId,
        status,
        notes,
        tagIds,
        tagNames,
        addTagIds,
        addTagNames,
        deploymentRingId,
      }),
    {
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries([DEVICE_QUERY_KEY, variables.deviceId]);
        await queryClient.invalidateQueries([
          DEVICE_READINESS_SUMMARY_QUERY_KEY,
          variables.deviceId,
        ]);
        await queryClient.invalidateQueries([DEVICE_READINESS_APPS_QUERY_KEY, variables.deviceId]);
        await queryClient.invalidateQueries([
          DEVICE_READINESS_UNMATCHED_QUERY_KEY,
          variables.deviceId,
        ]);
        await queryClient.invalidateQueries({ queryKey: [DEVICES_QUERY_KEY], exact: false });
        await queryClient.invalidateQueries([APP_COUNTS_KEY, customerEnvId]);
        // A ring change moves the device between rings' device counts and device lists.
        await queryClient.invalidateQueries({
          queryKey: [DEPLOYMENT_RINGS_QUERY_KEY],
          exact: false,
        });
        await queryClient.invalidateQueries({
          queryKey: [DEPLOYMENT_RING_DEVICES_QUERY_KEY],
          exact: false,
        });
      },
    },
  );
};

export const useDeviceReadinessSummary = ({ deviceId, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [DEVICE_READINESS_SUMMARY_QUERY_KEY, deviceId],
    () => getDeviceReadinessSummary({ deviceId, customerEnvId }),
    {
      enabled: enabled && !!deviceId && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useInfiniteDeviceReadinessApps = ({ deviceId, limit = 10, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [DEVICE_READINESS_APPS_QUERY_KEY, deviceId, customerEnvId],
    ({ pageParam = 1 }) =>
      getDeviceReadinessApps({
        deviceId,
        customerEnvId,
        page: pageParam,
        limit,
      }),
    {
      enabled: enabled && !!deviceId && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
        return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useInfiniteDeviceReadinessUnmatched = ({ deviceId, limit = 10, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [DEVICE_READINESS_UNMATCHED_QUERY_KEY, deviceId, customerEnvId],
    ({ pageParam = 1 }) =>
      getDeviceReadinessUnmatched({
        deviceId,
        customerEnvId,
        page: pageParam,
        limit,
      }),
    {
      enabled: enabled && !!deviceId && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
        return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useInfiniteDeviceWdacResults = ({ deviceId, apiQuery, limit, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [DEVICE_WDAC_RESULTS_QUERY_KEY, deviceId, customerEnvId, apiQuery, limit],
    ({ pageParam = 1, signal }) =>
      getDeviceWdacResults({
        deviceId,
        customerEnvId,
        page: pageParam,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!deviceId && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
        return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useInfiniteDeviceUnmatchedFiles = ({ deviceId, apiQuery, limit, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [DEVICE_UNMATCHED_FILES_QUERY_KEY, deviceId, customerEnvId, apiQuery, limit],
    ({ pageParam = 1, signal }) =>
      getDeviceUnmatchedFiles({
        deviceId,
        customerEnvId,
        page: pageParam,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!deviceId && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
        return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useDeviceUnmatchedFileDetails = ({ deviceId, fileId, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [DEVICE_UNMATCHED_FILE_DETAILS_QUERY_KEY, deviceId, customerEnvId, fileId],
    ({ signal }) =>
      getDeviceUnmatchedFileDetails({
        deviceId,
        customerEnvId,
        fileId,
        signal,
      }),
    {
      enabled: enabled && !!deviceId && !!customerEnvId && !!fileId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useInfiniteDeviceUnmatchedFileEvents = ({
  deviceId,
  fileId,
  limit = 10,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [DEVICE_UNMATCHED_FILE_DETAILS_QUERY_KEY, 'events', deviceId, customerEnvId, fileId, limit],
    ({ pageParam = 1, signal }) =>
      getDeviceUnmatchedFileEvents({
        deviceId,
        customerEnvId,
        fileId,
        page: pageParam,
        limit,
        signal,
      }),
    {
      enabled: enabled && !!deviceId && !!customerEnvId && !!fileId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
        return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useDeviceTabsDataCounts = ({ deviceId }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [DEVICE_TABS_DATA_COUNTS_QUERY_KEY, deviceId],
    () => getDeviceTabsDataCounts({ deviceId, customerEnvId }),
    {
      enabled: !!deviceId && !!customerEnvId,
    },
  );
};

export const useWdacReadinessStatusCounts = ({ apiQuery = {}, enabled = true } = {}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [DEVICE_WDAC_READINESS_COUNTS_QUERY_KEY, customerEnvId, apiQuery],
    () => getWdacReadinessStatusCounts({ customerEnvId, query: apiQuery }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useInfiniteWdacReadinessDevices = ({ apiQuery, limit = 10, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [DEVICE_WDAC_READINESS_QUERY_KEY, customerEnvId, apiQuery, limit],
    ({ pageParam = 1, signal }) =>
      getWdacReadinessDevices({
        customerEnvId,
        page: pageParam,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.devices?.length || 0), 0);
        return loaded < (lastPage.count || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};
